import { List, Map } from "immutable";

import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import Course, {
  Authors,
  AverageScorePercentiles,
  CourseId,
  References,
  SubscriptionTier,
  Tags
} from "../../../models/state/courses/course/Course";
import { Courses } from "../../../models/state/courses/Courses";
import { SectionId } from "../../../models/state/sections/section/Section";
import * as courseSelectors from "./course";

export function getCourse(state: Courses, courseId: CourseId) {
  return state.get(courseId);
}

export const getCourseName: (courses: Courses, courseId: CourseId) => string =
  composeCourseSelector(courseSelectors.getCourseName);

export const getCourseTextToSpeechEnabled: (
  courses: Courses,
  courseId: CourseId
) => boolean = composeCourseSelector(
  courseSelectors.getCourseTextToSpeechEnabled
);

export const getCourseVocabularyEnabled: (
  courses: Courses,
  courseId: CourseId
) => boolean = composeCourseSelector(
  courseSelectors.getCourseVocabularyEnabled
);

export const getCourseNames = (state: Courses, courseIds: List<CourseId>) => {
  return courseIds.reduce((map, courseId) => {
    if (!courseExists(state, courseId)) {
      return map;
    }

    return map.set(courseId, getCourseName(state, courseId));
  }, Map());
};

export const getAveragePercentilesForCourse: (
  courses: Courses,
  courseId: CourseId
) => AverageScorePercentiles | undefined = composeCourseSelector(
  courseSelectors.getCourseAverageScorePercentiles
);

export const getAveragePercentilesForCourses = (
  state: Courses,
  courseIds: List<CourseId>
) => {
  return courseIds.reduce((map, courseId) => {
    if (!courseExists(state, courseId)) {
      return map;
    }

    return map.set(courseId, getAveragePercentilesForCourse(state, courseId));
  }, Map<string, AverageScorePercentiles | undefined>());
};

export const getCourseDescription: (
  courses: Courses,
  courseId: CourseId
) => string = composeCourseSelector(courseSelectors.getCourseDescription);

export const getCourseAuthors: (
  courses: Courses,
  courseId: CourseId
) => Authors = composeCourseSelector(courseSelectors.getCourseAuthors);

export const getCourseReferences: (
  courses: Courses,
  courseId: CourseId
) => References = composeCourseSelector(courseSelectors.getCourseReferences);

// @ts-ignore
export const getCourseHeaderImageURL: (
  courses: Courses,
  courseId: CourseId
) => string = composeCourseSelector(courseSelectors.getCourseHeaderImageURL);

export const getCourseAvailable: (
  courses: Courses,
  courseId: CourseId
) => boolean = composeCourseSelector(courseSelectors.getCourseAvailable);

export const getCourseSectionIds: (
  courses: Courses,
  courseId: CourseId
) => List<SectionId> = composeCourseSelector(
  courseSelectors.getCourseSectionIds
);

export const getIsCoursePublic: (
  courses: Courses,
  courseId: CourseId
) => boolean = composeCourseSelector(courseSelectors.getIsCoursePublic);

export const getCourseSubscriptionTier: (
  courses: Courses,
  courseId: CourseId | null
) => SubscriptionTier | null = composeCourseSelector(
  courseSelectors.getCourseSubscriptionTier
);

export const courseIsFree: (courses: Courses, courseId: CourseId) => boolean =
  composeCourseSelector(courseSelectors.courseIsFree);

export const courseIsPremium: (
  courses: Courses,
  courseId: CourseId
) => boolean = composeCourseSelector(courseSelectors.courseIsPremium);

export const getCourseStartingSectionId: (
  courses: Courses,
  courseId: CourseId
) => SectionId | null | undefined = composeCourseSelector(
  courseSelectors.getCourseStartingSectionId
);

export const getCourseTagIds: (courses: Courses, courseId: CourseId) => Tags =
  composeCourseSelector(courseSelectors.getCourseTagIds);

export const getRelatedCourseIds: (
  courses: Courses,
  courseId: CourseId
) => List<CourseId> = composeCourseSelector(
  courseSelectors.getRelatedCourseIds
);

export const getCourseRegion: (
  courses: Courses,
  courseId: CourseId
) => string | undefined = composeCourseSelector(
  courseSelectors.getCourseRegion
);

export function getAllCourseIds(courses: Courses) {
  return courses.toList().map(courseSelectors.getCourseId);
}

export function getAllCourseIdsWithoutPremium(courses: Courses) {
  return courses
    .toList()
    .filter(course => {
      return (
        !courseIsPremium(courses, course.courseId!) &&
        !getCourseName(courses, course.courseId!)
          .toLowerCase()
          .includes("premium")
      );
    })
    .map(courseSelectors.getCourseId);
}

export function getAllCoursesCount(courses: Courses): number {
  return courses.size;
}

export function courseExists(courses: Courses, courseId: CourseId): boolean {
  return courses.has(courseId);
}

export function getRelatedPremiumCourseIdsForCourses(
  courses: Courses,
  courseIds: List<string>
): List<string> {
  return List(
    courseIds.map((courseId: string) =>
      (getRelatedCourseIds(courses, courseId) ?? List()).filter(
        (relatedCourse: string) =>
          courseIsPremium(courses, relatedCourse) &&
          !courseIds.some((courseId: string) => relatedCourse === courseId)
      )
    )
  )
    .flatten()
    .toSet()
    .toList() as List<string>;
}

export const getCourseExamQuestionsCount = composeCourseSelector(
  courseSelectors.getCourseExamQuestionsCount
);
export const doesCourseHaveExamQuestions = composeCourseSelector(
  courseSelectors.doesCourseHaveExamQuestions
);

export const doesCourseHaveVocabulary = composeCourseSelector(
  courseSelectors.doesCourseHaveVocabulary
);

function composeCourseSelector<T>(
  selector: (course: Course) => T
): (courses: Courses, courseId: string | null) => T {
  // @ts-ignore
  return composeSelector(getCourse, selector);
}
